import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { WindowComponent } from '@ibc-shared/components/window/window.component';
import { ModalComponent } from '@ibc/shared/components/modal/modal.component';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { RegistrationStatus } from '../models/preregistration-status.model';
import { RegistrationModel } from '../models/registration.model';
import { AuthService } from '../services/auth.service';
import { RegistrationService } from '../services/registration.service';
import { UserService } from '../services/users.service';

@Component({
  selector: 'app-preregistration',
  imports: [ReactiveFormsModule, ModalComponent, WindowComponent, TranslateModule],
  templateUrl: './preregistration.component.html',
  styleUrl: './preregistration.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreregistrationComponent {
  private registrationService = inject(RegistrationService);
  private userService = inject(UserService);
  private authService = inject(AuthService);

  protected registrationForm: FormGroup = new FormGroup({
    hasFursuit: new FormControl(false, { nonNullable: true }),
    wantsTshirt: new FormControl(false, { nonNullable: true }),
    ticketType: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    tshirtSize: new FormControl('none'),
    notes: new FormControl(null),
    acceptTerms: new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
  });
  protected submitted = signal(false);
  protected pending = signal(false);
  protected errorMessage = signal<string | null>(null);
  protected preregistrationComplete = signal<boolean>(false);

  // Para controlar el modal
  protected previewOpen = signal<boolean>(false);
  protected formValue = toSignal(this.registrationForm.valueChanges, {
    initialValue: this.registrationForm.value,
  });
  protected showTshirtSize = computed(() => {
    const { ticketType, wantsTshirt } = this.formValue();
    return ticketType === 'GOLD' || wantsTshirt;
  });

  public constructor() {
    effect(() => {
      const shouldRequireSize = this.showTshirtSize();
      const tshirtControl = this.registrationForm.get('tshirtSize');

      if (!tshirtControl) return;

      if (shouldRequireSize) {
        tshirtControl.setValidators([Validators.required, this.notNoneValidator()]);
      } else {
        tshirtControl.clearValidators();
        tshirtControl.setValue(null);
      }

      tshirtControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  protected getUserStatus(): RegistrationStatus | undefined {
    return this.userService.currentRegistration()?.data?.status;
  }

  protected isInvalid(controlName: string): boolean {
    const control = this.registrationForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched || this.submitted()));
  }

  protected onPreviewClick() {
    this.previewOpen.set(true);
  }

  protected onSubmit(): void {
    this.submitted.set(true);
    this.errorMessage.set(null);

    if (this.registrationForm.invalid || this.pending()) return;

    const user = this.authService.currentUser();
    const userId = user?.id;

    if (!userId) {
      this.errorMessage.set('features.preregister.form.errors.noUser');
      return;
    }

    const formValue = this.registrationForm.getRawValue();

    const payload: RegistrationModel & { userId: string | number } = {
      ...formValue,
      tshirtSize: formValue.tshirtSize === 'none' ? null : formValue.tshirtSize,
      notes: formValue.notes?.trim() ? formValue.notes.trim() : null,
      userId,
    };

    this.pending.set(true);
    this.registrationService
      .createRegistration(payload)
      .pipe(finalize(() => this.pending.set(false)))
      .subscribe({
        next: () => {
          this.preregistrationComplete.set(true);
          // this.registrationForm.reset({ hasFursuit: false, wantsTshirt: false, acceptTerms: false });
        },
        error: () => {
          this.errorMessage.set('features.preregister.form.errors.submitFailed');
        },
      });
  }

  public notNoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const v = control.value;
      return v && v !== 'none' ? null : { noneSelected: true };
    };
  }
}
